import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, HorizontalMenuTabs, PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { usePlaylistNuggets } from '@backrs_ui/grow-ui/src/hooks/usePlaylistNuggets';

import { Box, Theme, SxProps, Skeleton } from '@mui/material';

export type SuggestedNuggetsSidebarProps = Readonly<{
  pageType: 'nuggetPage' | 'competencyPage';
  nuggets: ContentNugget[];
  playlists?: PriorityPlaylist[];
  isLoading?: boolean;
  error?: unknown;
}>;

const STRINGS = appStrings.modules.suggestedNuggetsSidebar;

export const SuggestedNuggetsSidebar = ({
  pageType,
  nuggets,
  playlists = [],
  isLoading,
  error,
}: SuggestedNuggetsSidebarProps) => {
  const renderSkeleton = () => (
    <Box sx={skeletonHeadingContainer}>
      <Skeleton variant="text" sx={skeletonHeading} />
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} variant="text" sx={skeletonItem} />
      ))}
    </Box>
  );
  const navigate = useNavigate();

  const { playlistNuggets, handlePlaylistNuggetClick } = usePlaylistNuggets(playlists);

  const allNuggets = useMemo(() => [...playlistNuggets, ...nuggets], [playlistNuggets, nuggets]);

  const handleNuggetClick = (nugget: ContentNugget) => {
    if (nugget.variant === 'playlist') {
      handlePlaylistNuggetClick(nugget);

      return;
    }

    handlePanelNuggetClick(nugget, navigate);
  };

  return (
    <Box sx={suggestedNuggetsSidebar}>
      <HorizontalMenuTabs
        id="suggested-nuggets-tabs"
        tabs={[STRINGS.title[pageType]]}
        value={0}
        setValue={() => undefined}
        variant="minimal"
        containerSxOverride={tabsContainerSx}
      />
      {isLoading || error ? (
        renderSkeleton()
      ) : (
        <NuggetCardList nuggets={allNuggets} onClick={handleNuggetClick} direction="scrollingRow" />
      )}
    </Box>
  );
};

const suggestedNuggetsSidebar: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-3xl'],
});

const tabsContainerSx: SxProps<Theme> = {
  maxWidth: '100% !important',
  mx: '0 !important',
  display: 'flex !important',
};

const skeletonHeading: SxProps<Theme> = ({ backrs }) => ({
  width: '60%',
  height: 32,
  mb: backrs.spacing['spacing-md'],
});

const skeletonItem: SxProps<Theme> = ({ backrs }) => ({
  width: '90%',
  height: 24,
  mb: backrs.spacing['spacing-sm'],
});

const skeletonHeadingContainer: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-sm'],
});
